<template>
  <div class="field-badge-association">
    <v-btn v-if="!opened && !done" x-small outlined color="primary" @click="open">
      <v-icon left x-small>mdi-account-plus</v-icon>{{ $t('onboarding.field.badge-association.associate') }}
    </v-btn>

    <!-- À qui : le membre de la commande proposé d'office ; sinon un membre de la borne, par son nom. -->
    <div v-else-if="!done" class="field-badge-association__form">
      <v-autocomplete
          v-model="memberId"
          :items="members"
          :search-input.sync="memberSearch"
          :loading="searching"
          item-value="id"
          item-text="name"
          no-filter
          hide-no-data
          outlined
          dense
          hide-details
          background-color="white"
          prepend-inner-icon="mdi-account-search"
          :label="$t('onboarding.field.badge-association.member')"
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.organization">{{ item.organization }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
      <v-switch v-model="corporate" dense hide-details class="mt-2" :label="$t(corporate ? 'onboarding.field.badge-association.corporate' : 'onboarding.field.badge-association.personal')" />
      <div class="d-flex mt-2">
        <v-btn x-small text @click="opened = false">{{ $t('onboarding.field.badge-association.cancel') }}</v-btn>
        <v-spacer />
        <v-btn x-small depressed color="primary" :disabled="!memberId" :loading="working" @click="associate(false)">{{ $t('onboarding.field.badge-association.confirm') }}</v-btn>
      </div>
    </div>

    <div v-if="done" class="text-body-2 mt-1">
      <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>{{ doneLabel }}
    </div>
    <div v-if="error" class="text-caption error--text mt-1">{{ error }}</div>
  </div>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'

const CONFIRMATIONS = ['badge.enrollment.external-confirmation-required', 'badge.enrollment.out-of-stock-confirmation-required']

/**
 * Associer sur place un badge sans titulaire (lu au test de badge) à un membre de la borne : par le seul chemin
 * d'enregistrement, avec les règles du gestionnaire. Un badge que le réseau ne connaît pas se confirme (externe, sans
 * itinérance). Pour une pose, le membre de la commande est proposé d'office.
 */
export default {
  name: 'FieldBadgeAssociation',

  mixins: [CommissioningMixin],

  props: {
    interventionId: { type: String, required: true },
    uid: { type: String, required: true },
  },

  data() {
    return { opened: false, members: [], memberId: null, memberSearch: null, searching: false, searchTimer: null, corporate: true, working: false, error: null, done: null }
  },

  computed: {
    selectedMemberName() {
      return this.members.find(m => m.id === this.memberId)?.name || null
    },

    doneLabel() {
      const kind = this.done.private
        ? this.$t('onboarding.field.badge-association.external')
        : this.$t(this.done.inStock ? 'onboarding.field.badge-association.network' : 'onboarding.field.badge-association.known')
      return this.$t('onboarding.field.badge-association.done', { member: this.done.memberName, kind })
    },
  },

  watch: {
    memberSearch(text) {
      clearTimeout(this.searchTimer)
      if (!this.opened || (text && text === this.selectedMemberName)) {
        return
      }
      this.searchTimer = setTimeout(() => this.search(text), 300)
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    async open() {
      this.opened = true
      await this.search('')
    },

    async search(text) {
      this.searching = true
      this.error = null
      try {
        const { items, suggested } = await FieldRepository.testMembers(this.interventionId, text || '')
        this.members = suggested && !items.some(m => m.id === suggested.id) ? [suggested, ...items] : items
        if (suggested && !this.memberId) {
          this.memberId = suggested.id
        }
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.searching = false
      }
    },

    async associate(confirmExternal) {
      this.working = true
      this.error = null
      try {
        this.done = await FieldRepository.associateBadge(this.interventionId, { uid: this.uid, memberId: this.memberId, corporate: this.corporate, confirmExternal })
        this.$emit('associated', this.done)
      } catch (e) {
        // Inconnu du réseau : le serveur demande la confirmation, déjà traduite.
        if (!confirmExternal && CONFIRMATIONS.includes(e?.response?.data?.messageKey)) {
          const confirmed = await this.$dialog.confirm({ text: e.response.data.message })
          if (confirmed) {
            await this.associate(true)
          }
          return
        }
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.working = false
      }
    },
  },
}
</script>

<style scoped>
.field-badge-association {
  margin-top: 8px;
}

.field-badge-association__form {
  padding: 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
}
</style>
